import React from "react";
import { FaSpinner, FaLock, FaUsers, FaBug } from "react-icons/fa";
import useTeamAccess from "../hooks/useTeamAccess";

const TeamAccessWrapper = ({
  children,
  moduleName = "Module",
  showDebugToggle = false,
  debugEnabled = false,
  onDebugToggle = null,
}) => {
  const {
    teams,
    selectedTeam,
    accessState,
    needsAuth,
    hasNoTeams,
    needsTeamSelection,
    isLoading,
    isReady,
    selectTeam,
    switchToTeam,
    clearTeamSelection,
  } = useTeamAccess();

  // Loading state
  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">{moduleName}</h1>
        <div className="p-4 bg-blue-50 text-blue-700 rounded-lg flex items-center">
          <FaSpinner className="animate-spin mr-3" />
          Loading authentication state...
        </div>
      </div>
    );
  }

  // No authentication
  if (needsAuth) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <FaLock className="text-4xl text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">
            Authentication Required
          </h2>
          <p className="text-gray-600">
            Please log in to access the {moduleName} module.
          </p>
        </div>
      </div>
    );
  }

  // No teams
  if (hasNoTeams) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">{moduleName}</h1>

          {/* Debug Toggle */}
          {showDebugToggle && onDebugToggle && (
            <div className="flex items-center space-x-2">
              <FaBug
                className={`${
                  debugEnabled ? "text-green-600" : "text-gray-400"
                }`}
              />
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={debugEnabled}
                  onChange={(e) => onDebugToggle(e.target.checked)}
                  className="form-checkbox h-4 w-4 text-purple-600"
                />
                <span className="text-sm text-gray-700">Debug Logging</span>
              </label>
            </div>
          )}
        </div>

        <div className="text-center py-10 bg-gray-50 rounded-lg">
          <FaUsers className="mx-auto text-4xl text-gray-400 mb-3" />
          <p className="text-gray-600 mb-4">
            Not part of any team - please contact admin for help
          </p>
        </div>
      </div>
    );
  }

  // Team selection needed
  if (needsTeamSelection) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">{moduleName}</h1>

          {/* Debug Toggle */}
          {showDebugToggle && onDebugToggle && (
            <div className="flex items-center space-x-2">
              <FaBug
                className={`${
                  debugEnabled ? "text-green-600" : "text-gray-400"
                }`}
              />
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={debugEnabled}
                  onChange={(e) => onDebugToggle(e.target.checked)}
                  className="form-checkbox h-4 w-4 text-purple-600"
                />
                <span className="text-sm text-gray-700">Debug Logging</span>
              </label>
            </div>
          )}
        </div>

        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">Select a Team</h3>
          <div className="space-y-3">
            {teams.map((team) => (
              <div
                key={team.id}
                className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => selectTeam(team)}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-medium text-lg">{team.name}</h4>
                    <p className="text-sm text-gray-600">
                      {team.role === "owner"
                        ? "Team Owner"
                        : team.role === "creator"
                        ? "Creator Access"
                        : "Reader Access"}
                    </p>
                  </div>
                  <div className="text-sm text-gray-500">
                    {team.createdAt?.toDate
                      ? new Date(team.createdAt.toDate()).toLocaleDateString()
                      : team.createdAt instanceof Date
                      ? team.createdAt.toLocaleDateString()
                      : "Date unknown"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Ready - render children with team context
  if (isReady) {
    return children({
      selectedTeam,
      teams,
      clearTeamSelection,
      switchToTeam,
      showDebugToggle,
      debugEnabled,
      onDebugToggle,
    });
  }

  // Fallback
  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="p-4 bg-red-50 text-red-700 rounded-lg">
        Unexpected access state: {accessState}
      </div>
    </div>
  );
};

export default TeamAccessWrapper;
